import {
  AppShellContent,
  Button,
  Checkbox,
  Group,
  Input,
  Progress,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
  Title,
  TooltipIcon,
  toast,
  Upload,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { UnsavedChangesDialog } from "../components/UnsavedChangesDialog";
import { useUnsavedChangesGuard } from "../components/useUnsavedChangesGuard";
import { useWorkScope } from "../components/WorkScopeProvider";
import { getWorkScopeSearch } from "../lib/workSearch";
import {
  buildProcedureCandidate,
  createProcedureFromForm,
  type ProcedureBuilder,
  type ProcedureDraft,
  previewProcedureBuilder,
  procedureDraftSchema,
  publishProcedureCandidates,
  saveProcedureDraft,
  selectProcedureCandidates,
  useFacilityAssetsQuery,
  useProcedureBuilderQuery,
} from "../queries/useProceduresQuery";

const PROCEDURE_TYPES = [
  { label: "Safety", value: "Safety" },
  { label: "Maintenance", value: "Maintenance" },
  { label: "Operations", value: "Operations" },
  { label: "Reliability", value: "Reliability" },
];

const FREQUENCIES = [
  { label: "Daily", value: "Daily" },
  { label: "Weekly", value: "Weekly" },
  { label: "Monthly", value: "Monthly" },
  { label: "Quarterly", value: "Quarterly" },
  { label: "Yearly", value: "Yearly" },
  { label: "Other", value: "Other" },
];

function sourceLabel(item: { source: { sheet?: string; cells?: string[] } }) {
  return [item.source.sheet, item.source.cells?.join(", ")]
    .filter(Boolean)
    .join(" · ");
}

type DraftListKey = "instructions" | "tools" | "cautions";

function DraftListEditor({
  draft,
  listKey,
  onChange,
}: {
  draft: ProcedureDraft;
  listKey: DraftListKey;
  onChange: (next: ProcedureDraft) => void;
}) {
  const items = draft[listKey];
  const label = listKey[0].toUpperCase() + listKey.slice(1);
  return (
    <Stack gap={8}>
      <Group justify="between">
        <Text>{label}</Text>
        <Button
          onClick={() =>
            onChange({
              ...draft,
              [listKey]: [
                ...items,
                {
                  text: "",
                  source: { documentType: "xlsx" },
                  review: "needs_review",
                },
              ],
            })
          }
          size="sm"
          variant="outline"
        >
          Add
        </Button>
      </Group>
      {items.map((item, index) => (
        <Group
          align="start"
          gap={8}
          key={`${listKey}-${item.source.sheet ?? "manual"}-${item.source.cells?.join("-") ?? "item"}-${item.text}`}
        >
          <div className="min-w-0 flex-1">
            <Textarea
              aria-label={`${label} ${index + 1}`}
              onChange={(event) =>
                onChange({
                  ...draft,
                  [listKey]: items.map((current, itemIndex) =>
                    itemIndex === index
                      ? { ...current, text: event.target.value }
                      : current,
                  ),
                })
              }
              value={item.text}
            />
            <Text tone="subtle">
              Source: {sourceLabel(item) || "manual entry"}
            </Text>
          </div>
          <Stack gap={4}>
            <Button
              aria-label={`Move ${label} ${index + 1} up`}
              disabled={index === 0}
              onClick={() => {
                const next = [...items];
                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                onChange({ ...draft, [listKey]: next });
              }}
              size="sm"
              variant="ghost"
            >
              ↑
            </Button>
            <Button
              aria-label={`Move ${label} ${index + 1} down`}
              disabled={index === items.length - 1}
              onClick={() => {
                const next = [...items];
                [next[index], next[index + 1]] = [next[index + 1], next[index]];
                onChange({ ...draft, [listKey]: next });
              }}
              size="sm"
              variant="ghost"
            >
              ↓
            </Button>
            <Button
              aria-label={`Delete ${label} ${index + 1}`}
              onClick={() =>
                onChange({
                  ...draft,
                  [listKey]: items.filter(
                    (_, itemIndex) => itemIndex !== index,
                  ),
                })
              }
              size="sm"
              variant="ghost"
            >
              Delete
            </Button>
          </Stack>
        </Group>
      ))}
    </Stack>
  );
}

function DraftEditor({
  candidateId,
  initialDraft,
  builder,
  onSaved,
}: {
  candidateId: string;
  initialDraft: ProcedureDraft;
  builder: ProcedureBuilder;
  onSaved: (builder: ProcedureBuilder) => void;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const [draft, setDraft] = useState(initialDraft);
  const draftRef = useRef(initialDraft);
  const [savedDraft, setSavedDraft] = useState(initialDraft);
  const savedDraftRef = useRef(initialDraft);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const hasUnsavedChanges =
    JSON.stringify(draft) !== JSON.stringify(savedDraft);
  const unsavedChangesDialog = useUnsavedChangesGuard({
    hasUnsavedChanges,
    onSave: save,
  });
  useEffect(() => {
    const hasLocalEdits =
      JSON.stringify(draftRef.current) !==
      JSON.stringify(savedDraftRef.current);
    if (!hasLocalEdits) {
      draftRef.current = initialDraft;
      setDraft(initialDraft);
    }
    savedDraftRef.current = initialDraft;
    setSavedDraft(initialDraft);
  }, [initialDraft]);
  function updateDraft(next: ProcedureDraft) {
    draftRef.current = next;
    setDraft(next);
    setSaveSuccess(false);
  }
  /** Resolves true when the draft was saved. */
  async function save(): Promise<boolean> {
    if (!selectedCustomer || !selectedFacility) return false;
    const draftToSave = procedureDraftSchema.parse(draft);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      onSaved(
        await saveProcedureDraft(
          selectedCustomer.id,
          selectedFacility.id,
          builder.id,
          candidateId,
          draftToSave,
        ),
      );
      setDraft((current) =>
        JSON.stringify(current) === JSON.stringify(draftToSave)
          ? draftToSave
          : current,
      );
      savedDraftRef.current = draftToSave;
      setSavedDraft(draftToSave);
      const newerEditsRemain =
        JSON.stringify(draftRef.current) !== JSON.stringify(draftToSave);
      setSaveSuccess(!newerEditsRemain);
      toast.add(
        newerEditsRemain
          ? "Draft saved. Newer edits remain unsaved."
          : "Draft saved successfully.",
        { icon: true },
      );
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "The draft could not be saved.";
      setSaveError(errorMessage);
      toast.error(errorMessage, { icon: true });
      return false;
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <Tile padding="compact" width="full">
        <Stack gap={16}>
          <Title as="h3" size="lg">
            Review draft
          </Title>
          <Input
            label="Procedure name"
            onChange={(event) =>
              updateDraft({ ...draft, name: event.target.value })
            }
            value={draft.name}
          />
          <Textarea
            label="Description"
            onChange={(event) =>
              updateDraft({ ...draft, description: event.target.value || null })
            }
            value={draft.description ?? ""}
          />
          <DraftListEditor
            draft={draft}
            listKey="instructions"
            onChange={updateDraft}
          />
          <DraftListEditor
            draft={draft}
            listKey="tools"
            onChange={updateDraft}
          />
          <DraftListEditor
            draft={draft}
            listKey="cautions"
            onChange={updateDraft}
          />
          {draft.warnings.length ? (
            <Text tone="warning">Warnings: {draft.warnings.join("; ")}</Text>
          ) : null}
          {saveError ? <Text tone="negative">{saveError}</Text> : null}
          {saveSuccess ? <Text tone="positive">Draft saved.</Text> : null}
          <Button loading={saving} onClick={() => void save()} size="sm">
            Save draft
          </Button>
        </Stack>
      </Tile>
      <UnsavedChangesDialog {...unsavedChangesDialog} />
    </>
  );
}

export function ProcedureBuilderScreen({ builderId }: { builderId?: string }) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const navigate = useNavigate();
  const query = useProcedureBuilderQuery(builderId);
  const assetsQuery = useFacilityAssetsQuery();
  const [builder, setBuilder] = useState<ProcedureBuilder | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputCandidateId, setOutputCandidateId] = useState<string | null>(
    null,
  );
  const outputRef = useRef<HTMLDivElement>(null);
  const [showFormMode, setShowFormMode] = useState(true);
  const [formTitle, setFormTitle] = useState("");
  const [formAssetId, setFormAssetId] = useState<string | null>(null);
  const [formType, setFormType] = useState<string | null>(null);
  const [formFrequency, setFormFrequency] = useState<string | null>(null);
  const [formDescription, setFormDescription] = useState("");
  const persistedSelection =
    builder?.candidates
      .filter((candidate) => candidate.selected)
      .map((candidate) => candidate.id) ?? [];
  const hasUnsavedBuilderChanges = Boolean(
    (!builder &&
      !builderId &&
      showFormMode &&
      (formTitle.trim() ||
        formAssetId ||
        formType ||
        formFrequency ||
        formDescription.trim())) ||
      (builder &&
        [...selected].sort().join("\u0000") !==
          [...persistedSelection].sort().join("\u0000")),
  );
  const unsavedChangesDialog = useUnsavedChangesGuard({
    hasUnsavedChanges: hasUnsavedBuilderChanges,
  });

  // Only syncs from the query on first load or when it's actually a
  // different builder. Every other builder update already flows through an
  // explicit setBuilder call (upload, form submit, select/build candidates,
  // publish, or the refetch-after-error fallback below) — resyncing on every
  // query.data change would let an incidental background refetch (e.g. on
  // window focus) silently clobber those in-progress, not-yet-saved edits.
  useEffect(() => {
    if (query.data && query.data.id !== builder?.id) {
      setBuilder(query.data);
      setSelected(
        query.data.candidates
          .filter((candidate) => candidate.selected)
          .map((candidate) => candidate.id),
      );
    }
  }, [query.data, builder?.id]);

  useEffect(() => {
    if (
      !outputCandidateId ||
      !outputRef.current ||
      !builder?.candidates.some(
        (candidate) => candidate.id === outputCandidateId && candidate.draft,
      )
    ) {
      return;
    }
    outputRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    setOutputCandidateId(null);
  }, [builder, outputCandidateId]);

  async function handleUpload(files: File[]) {
    const file = files[0];
    if (!file || !selectedCustomer || !selectedFacility) return;
    setBusy(true);
    setError(null);
    try {
      const next = await previewProcedureBuilder(
        selectedCustomer.id,
        selectedFacility.id,
        file,
      );
      setBuilder(next);
      void navigate({
        search: {
          builderId: next.id,
          procedureId: undefined,
          aiDraftId: undefined,
          ai: undefined,
          ...getWorkScopeSearch(selectedCustomer, selectedFacility),
        },
        to: "/procedures/builder",
      });
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "The workbook could not be uploaded.",
      );
    } finally {
      setBusy(false);
    }
  }

  function isFormValid() {
    return !!(formTitle.trim() && formAssetId && formType && formFrequency);
  }

  async function handleFormSubmit() {
    if (!selectedCustomer || !selectedFacility) {
      setError("A customer and facility must be selected.");
      return;
    }

    if (!isFormValid()) {
      const missingFields = [];
      if (!formTitle.trim()) missingFields.push("Title");
      if (!formAssetId) missingFields.push("Asset");
      if (!formType) missingFields.push("Type");
      if (!formFrequency) missingFields.push("Frequency");
      setError(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    setBusy(true);
    setError(null);

    if (!isFormValid()) return;

    const validAssetId = formAssetId as string;
    const validType = formType as string;
    const validFrequency = formFrequency as string;

    try {
      const next = await createProcedureFromForm(
        selectedCustomer.id,
        selectedFacility.id,
        {
          title: formTitle,
          assetId: validAssetId,
          type: validType,
          frequency: validFrequency,
          description: formDescription,
        },
      );
      setBuilder(next);
      setShowFormMode(false);
      toast.add(
        "Procedure created successfully. Review and publish when ready.",
        { icon: true },
      );
      void navigate({
        search: {
          builderId: next.id,
          procedureId: undefined,
          aiDraftId: undefined,
          ai: undefined,
          ...getWorkScopeSearch(selectedCustomer, selectedFacility),
        },
        to: "/procedures/builder",
      });
    } catch (submitError) {
      const errorMessage =
        submitError instanceof Error
          ? submitError.message
          : "Failed to create procedure. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, { icon: true });
    } finally {
      setBusy(false);
    }
  }

  const assetOptions =
    assetsQuery.data?.map((asset) => ({
      label: asset.name,
      value: String(asset.id),
    })) ?? [];

  async function updateSelection() {
    if (!builder || !selectedCustomer || !selectedFacility) return;
    setBusy(true);
    setError(null);
    try {
      setBuilder(
        await selectProcedureCandidates(
          selectedCustomer.id,
          selectedFacility.id,
          builder.id,
          selected,
        ),
      );
    } catch (selectionError) {
      setError(
        selectionError instanceof Error
          ? selectionError.message
          : "The candidate selection could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function buildCandidate(candidateId: string) {
    if (!builder || !selectedCustomer || !selectedFacility) return;
    setBusy(true);
    setError(null);
    try {
      const selectedBuilder = await selectProcedureCandidates(
        selectedCustomer.id,
        selectedFacility.id,
        builder.id,
        selected,
      );
      setBuilder(selectedBuilder);
      setBuilder(
        await buildProcedureCandidate(
          selectedCustomer.id,
          selectedFacility.id,
          selectedBuilder.id,
          candidateId,
        ),
      );
      setOutputCandidateId(candidateId);
    } catch (buildError) {
      setError(
        buildError instanceof Error
          ? buildError.message
          : "The Procedure draft could not be built.",
      );
      const refreshed = await query.refetch();
      if (refreshed.data) setBuilder(refreshed.data);
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    if (!builder || !selectedCustomer || !selectedFacility) return;
    setBusy(true);
    setError(null);
    try {
      await publishProcedureCandidates(
        selectedCustomer.id,
        selectedFacility.id,
        builder.id,
        selected,
      );
      toast.add("Procedures published successfully.", { icon: true });
      if (selectedCustomer && selectedFacility) {
        void navigate({
          search: getWorkScopeSearch(selectedCustomer, selectedFacility),
          to: "/procedures",
        });
      }
    } catch (publishError) {
      const errorMessage =
        publishError instanceof Error
          ? publishError.message
          : "The selected Procedures could not be published.";
      setError(errorMessage);
      toast.error(errorMessage, { icon: true });
    } finally {
      setBusy(false);
    }
  }

  if (!builderId && !builder && showFormMode)
    return (
      <AppShellContent>
        <Stack gap={24}>
          <Group justify="start">
            <Button
              variant="ghost"
              onClick={() => {
                if (selectedCustomer && selectedFacility) {
                  void navigate({
                    search: getWorkScopeSearch(
                      selectedCustomer,
                      selectedFacility,
                    ),
                    to: "/procedures",
                  });
                }
              }}
            >
              ← Back to Procedures
            </Button>
          </Group>

          <div className="text-center">
            <Stack gap={8}>
              <Title as="h1" size="xl">
                Build a Procedure with{" "}
                <span className="text-primary">Reli</span>
              </Title>
              <Text tone="subtle">
                Add details below, and we'll draft a complete procedure for you
                to review and edit.
              </Text>
            </Stack>
          </div>

          <div className="mx-auto w-full max-w-2xl rounded-(--radius-md) border border-outline-subtle bg-surface p-(--spacing-20)">
            <Stack gap={16}>
              {error ? <Text tone="negative">{error}</Text> : null}

              <Input
                label="Title"
                placeholder="e.g. Monthly HVAC Inspection"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                required
              />

              <div>
                <Select
                  label="Asset"
                  searchable
                  placeholder={
                    assetsQuery.isPending ? "Loading assets..." : "Search..."
                  }
                  options={assetsQuery.isPending ? [] : assetOptions}
                  value={formAssetId}
                  onChange={(value) => setFormAssetId(value ?? null)}
                  required
                  disabled={assetsQuery.isPending}
                />
                {assetsQuery.isPending && (
                  <div className="mt-1">
                    <Text tone="subtle" size="sm">
                      Loading assets...
                    </Text>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-(--spacing-12) md:grid-cols-2">
                <Select
                  label="Type"
                  options={PROCEDURE_TYPES}
                  value={formType}
                  onChange={(value) => setFormType(value ?? null)}
                  required
                />
                <Select
                  label="Frequency"
                  options={FREQUENCIES}
                  value={formFrequency}
                  onChange={(value) => setFormFrequency(value ?? null)}
                  required
                />
              </div>

              <Stack gap={4}>
                <Group gap={4}>
                  <label
                    htmlFor="procedure-description"
                    className="text-sm font-medium"
                  >
                    Describe your procedure
                  </label>
                  <TooltipIcon
                    label="Procedure description"
                    description="Provide details about what this procedure covers"
                    size="sm"
                  />
                </Group>
                <Textarea
                  id="procedure-description"
                  placeholder="e.g. Quarterly inspection of rooftop HVAC unit. Should cover refrigerant levels, filter replacement, and belt condition. Technician signature required at the end."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  maxLength={2000}
                />
                <Text tone="subtle" size="sm">
                  Add fields, topics, or considerations to include. More detail
                  leads to a better procedure.
                </Text>
              </Stack>

              <Button
                loading={busy}
                onClick={() => void handleFormSubmit()}
                disabled={!isFormValid()}
              >
                Build Procedure
              </Button>
              <Button
                disabled={busy}
                onClick={() => setShowFormMode(false)}
                variant="outline"
              >
                Use an XLSX workbook instead
              </Button>
            </Stack>
          </div>
        </Stack>
        <UnsavedChangesDialog {...unsavedChangesDialog} />
      </AppShellContent>
    );

  if (!builderId && !builder)
    return (
      <AppShellContent>
        <Stack gap={24}>
          <Group justify="start">
            <Button
              disabled={busy}
              onClick={() => setShowFormMode(true)}
              variant="ghost"
            >
              ← Back to form
            </Button>
          </Group>
          <Title as="h1" size="xl">
            Procedure Builder
          </Title>
          <Text>
            Upload an XLSX workbook to discover task-specific worksheet
            candidates.
          </Text>
          {error ? <Text tone="negative">{error}</Text> : null}
          <Upload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            acceptedFormatsLabel="XLSX"
            maxSizeLabel="10 MB"
            onFilesSelected={(files) => void handleUpload(files)}
            variant="file"
            buttonLabel="Choose workbook"
          />
        </Stack>
      </AppShellContent>
    );

  if (query.isPending && !builder)
    return (
      <AppShellContent>
        <Text tone="subtle">Loading Procedure Builder…</Text>
      </AppShellContent>
    );

  if (query.isError && !builder)
    return (
      <AppShellContent>
        <Text tone="negative">
          Unable to load this Procedure Builder review.
        </Text>
      </AppShellContent>
    );

  if (!builder) return null;

  return (
    <AppShellContent>
      <Stack gap={24}>
        <Group justify="between" wrap="wrap">
          <div>
            <Title as="h1" size="xl">
              Procedure Builder
            </Title>
            <Text tone="subtle">{builder.filename}</Text>
          </div>
          <Button
            disabled={busy || selected.length === 0}
            onClick={() => void publish()}
            size="sm"
          >
            Publish selected
          </Button>
        </Group>
        {error ? <Text tone="negative">{error}</Text> : null}
        {builder.documentWarnings.length ? (
          <Text tone="warning">
            Workbook warnings: {builder.documentWarnings.join("; ")}
          </Text>
        ) : null}
        {!builderId ? (
          <Upload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            acceptedFormatsLabel="XLSX"
            maxSizeLabel="10 MB"
            onFilesSelected={(files) => void handleUpload(files)}
            variant="file"
            buttonLabel="Choose another workbook"
          />
        ) : null}
        <Tile padding="compact" width="full">
          <Stack gap={12}>
            <Title as="h2" size="lg">
              Worksheet candidates
            </Title>
            {builder.candidates.map((candidate) => (
              <Group align="start" gap={12} key={candidate.id}>
                <Checkbox
                  aria-label={`Select ${candidate.displayName}`}
                  checked={selected.includes(candidate.id)}
                  onCheckedChange={(checked) =>
                    setSelected((current) =>
                      checked
                        ? [...new Set([...current, candidate.id])]
                        : current.filter((id) => id !== candidate.id),
                    )
                  }
                />
                <div className="min-w-0 flex-1">
                  <Text>{candidate.displayName}</Text>
                  <Text tone="subtle">
                    {candidate.sourceName} · {candidate.status}
                  </Text>
                  {candidate.status === "building" ? (
                    <Stack gap={4}>
                      <Text tone="subtle">Building draft…</Text>
                      <Progress
                        aria-label={`Building ${candidate.displayName}`}
                        value={50}
                      />
                    </Stack>
                  ) : null}
                  {candidate.warnings.map((warning) => (
                    <Text key={warning} tone="warning">
                      {warning}
                    </Text>
                  ))}
                  {candidate.errorMessage ? (
                    <Text tone="negative">{candidate.errorMessage}</Text>
                  ) : null}
                </div>
                <Button
                  disabled={busy || !selected.includes(candidate.id)}
                  onClick={() => void buildCandidate(candidate.id)}
                  size="sm"
                  variant="outline"
                >
                  {candidate.status === "failed"
                    ? "Retry build"
                    : "Build draft"}
                </Button>
              </Group>
            ))}
          </Stack>
        </Tile>
        <Button
          disabled={busy}
          onClick={() => void updateSelection()}
          size="sm"
          variant="outline"
        >
          Save candidate selection
        </Button>
        <div ref={outputRef}>
          <Stack gap={16}>
            {builder.candidates.flatMap((candidate) =>
              candidate.draft
                ? [
                    <DraftEditor
                      builder={builder}
                      candidateId={candidate.id}
                      initialDraft={candidate.draft}
                      key={candidate.id}
                      onSaved={setBuilder}
                    />,
                  ]
                : [],
            )}
          </Stack>
        </div>
      </Stack>
      <UnsavedChangesDialog {...unsavedChangesDialog} />
    </AppShellContent>
  );
}
